import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { LexicalNode } from "lexical";
import {
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  TextNode,
} from "lexical";
import { toPairs } from "lodash-es";
import { useCallback, useEffect } from "react";
import { notEmpty } from "../../../../../utils/not-empty";
import { $replaceWithMultiple } from "../../utils/replace-multiple";
import { getCurlyBraceBalance } from "./merge-tag-utils";

export const useAutoMergeTag = (createMergeTagFn: (tag: string) => LexicalNode | null): void => {
  const [editor] = useLexicalComposerContext();

  const textNodeOnDestroy = useCallback((): void => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (selection.hasFormat("highlight") && !selection.getTextContent().includes("{{")) {
        selection.toggleFormat("highlight");
      }
    });
  }, [editor]);

  const textNodeOnUpdate = useCallback(
    (key: string): void => {
      // If the node contains a merge tag, we want to extract the merge tag and
      // convert it to a merge tag node
      editor.update(() => {
        const node = $getNodeByKey(key);

        if (!$isTextNode(node)) return;

        const mergeTagRegex = new RegExp(/(\{{.*?\}})/g);

        // Now split the text node into multiple text nodes, and merge tag nodes
        const textNodeFragments = node.getTextContent().split(mergeTagRegex);
        if (textNodeFragments.length === 1) {
          const result = getCurlyBraceBalance(textNodeFragments[0]!);

          if (!result.balanced) {
            const selection = node.select(result.errorStartPos, result.errorStartPos + 2);

            if (!selection.hasFormat("highlight")) {
              selection.formatText("highlight");
            } else {
              node.select(node.getTextContentSize(), node.getTextContentSize());
            }
          }

          return;
        }

        const newNodes: LexicalNode[] = textNodeFragments
          .map((fragment) =>
            mergeTagRegex.test(fragment)
              ? createMergeTagFn(fragment.slice(2, -2))
              : $createTextNode(fragment).setFormat(node.getFormat()),
          )
          .filter(notEmpty);

        $replaceWithMultiple(node, newNodes);

        const lastNode = newNodes[newNodes.length - 1]!;
        const caretToEnd = lastNode.selectNext(0, 0);
        if (caretToEnd.hasFormat("highlight")) {
          caretToEnd.toggleFormat("highlight");
        }
      });
    },
    [editor, createMergeTagFn],
  );

  useEffect(() => {
    return editor.registerMutationListener(TextNode, (mutatedNodes) => {
      toPairs(mutatedNodes).map(([key, mutation]) => {
        mutation === "destroyed" ? textNodeOnDestroy() : textNodeOnUpdate(key);
      });
    });
  }, [editor, textNodeOnDestroy, textNodeOnUpdate]);
};
